import { useEffect, useState } from "react";
import Job from "../../models/Job";
import { DrawerForm } from "../../../../../components/drawers/DrawerForm";
import { getCountry, getDefaultActiveCountry, getUSA, useEnvironment, useHub } from "../../../../../core/Global";
import { API_ROUTES } from "../../../../../core/Constants";
import Map from "../../../../../components/here/Map";
import AddressField from "../../../../../components/here/AddressField";
import { HereGeocodeItem } from "../../../../../models/MapModels";
import TextBox from "../../../../../components/form/TextBox";
import Country from "../../../../base/models/Country";
import DropDown from "../../../../../components/form/DropDown";
import DateItem from "../../../../../models/DateItem";
import { toastError } from "../../../../../components/base/Toast";

interface IProps {
    job?: Job
    onClose?: () => void
}
export default function JobUpdateAddressModal(props: IProps) {

    const hub = useHub();
    const env = useEnvironment();

    const [states, statesSet] = useState<DateItem[]>();
    const [country, countrySet] = useState<Country>(getDefaultActiveCountry());
    const [address, addressSet] = useState<HereGeocodeItem>();
    const [selectedState, selectedStateSet] = useState<string>();
    const [zipcode, zipcodeSet] = useState<string>();
    const [customAddress, customAddressSet] = useState<string>("");

    const [zoom, zoomSet] = useState<number>()

    useEffect(() => {
        // Parsing a zip out of the typed text only makes sense for manual
        // (non-verified) addresses. A verified address owns its own state/zip
        // (handled by the [address] effect below) — don't let this clobber it.
        if (!window.isNullOrEmpty(address?.id)) return;

        var zipcode = "";
        var validChars = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '-']
        if (customAddress) {
            for (var i = 0; i < customAddress.length; i++) {
                var c = customAddress[i];
                if (validChars.indexOf(c) < 0) zipcode = "";
                else zipcode += c;

                if (zipcode.length > 4) break;
            }
        }

        if (zipcode.length > 4) {
            hub.LookUpZipcode(zipcode, country?.alpha3Code ?? "usa", res => {
                zipcodeSet(res?.address?.postalCode)
                selectedStateSet(res?.address?.stateId)
            })
        }
    }, [customAddress, address])

    useEffect(() => {
        if (country) {
            hub.Get("/base/states/?countryId=" + country.id, res => {
                if (res.data) statesSet(res.data);
            });
        }
    }, [country])

    useEffect(() => {
        if (props.job?.address) {
            var c = getCountry(props.job.address.address?.countryId);
            if (c) countrySet(c);
        }
        if (props.job) {
            addressSet(props.job.address)
            customAddressSet(props.job.address?.address?.label ?? "")
            // Show the saved state/zip on open — works for custom (non-verified)
            // addresses too, where they were entered by hand last time.
            zipcodeSet(props.job.address?.address?.postalCode ?? "")
            selectedStateSet(props.job.address?.address?.stateId)
        }

    }, [props.job]);

    // Whenever a Here-verified address is picked from above, mirror its own
    // state/zip into the (read-only) fields. When not verified we leave them
    // alone so the user can type them in manually.
    useEffect(() => {
        if (!window.isNullOrEmpty(address?.id)) {
            zipcodeSet(address?.address?.postalCode ?? "");
            selectedStateSet(address?.address?.stateId);
        }
    }, [address]);

    const [showMap, showMapSet] = useState<boolean>(false);

    // A Here-verified address carries its own state/zip, so we only ask for them
    // manually when the typed address wasn't verified (no address.id).
    const verified = !window.isNullOrEmpty(address?.id);

    const updateAddress = (callback: any) => {

        var valid = true;
        window.hideFormErrors();

        if (customAddress.length < 3) {
            window.showFormError("jobAddress", "Please write the job address!", false, "drawer-update-address");
            valid = false;
        }
        // A job must always carry a state & zip (verified addresses fill these
        // automatically; otherwise the user enters them by hand below).
        if (window.isNullOrEmpty(zipcode)) {
            window.showFormError("zipCode", "Please enter the zip code!", false, "drawer-update-address");
            valid = false;
        }
        if (window.isNullOrEmpty(selectedState)) {
            window.showFormError("stateId", "Please select the state!", false, "drawer-update-address");
            valid = false;
        }

        if (!valid) {
            // No res -> DrawerForm stops the spinner and keeps the drawer open.
            callback?.()
            return
        }

        // For a verified address, always submit ITS own state/zip (not the field
        // values, which could be stale) so the server stores them consistently
        // even if it has to fall back from an expired Here id.
        const submitZip = verified ? (address?.address?.postalCode ?? zipcode) : zipcode;
        const submitState = verified ? (address?.address?.stateId ?? selectedState) : selectedState;

        hub.Post(API_ROUTES.Jobs + "address", {
            id: props.job?.id,
            addressId: address?.id,
            customAddress: customAddress,
            zipCode: submitZip,
            stateId: submitState,
            countryId: country?.id ?? getUSA().id,
            zoom: zoom

        }, res => {
            if (!res.success) toastError(res.message)
            // Pass res back: on success DrawerForm closes the drawer, otherwise it
            // just stops the spinner and stays open.
            callback?.(res)
        });

    }

    return (
        <>

            <DrawerForm id="drawer-update-address"
                title="Address"
                subtitle={"Job ID: " + props.job?.jobId + " | " + props.job?.fullName}
                buttonText="Update Address"
                size="large" noPadding
                focusOn="jobupdateaddress-address"
                onOpen={() => showMapSet(true)}
                onClose={props.onClose}
                onSubmit={(body, callback) => {
                    updateAddress(callback)
                }}>

                <div className="mx-10 mt-5">
                    <AddressField id="jobupdateaddress-address" label="" name="jobAddress" labelUp
                        placeholder="Search address..." block verify
                        selectedAddress={address}
                        onTextChanged={e => {
                            customAddressSet(e)
                            window.hideFormError("jobAddress", "drawer-update-address");
                        }}
                        onCountrySelect={c => countrySet(c)}
                        onSelect={e => addressSet(e)}
                        onUnselect={() => addressSet(undefined)} />

                    {/* Always visible. When the address above is Here-verified these
                        mirror its data and lock; otherwise they're entered by hand. */}
                    <div className="row mt-3">
                        <div className="col-md-6">
                            <TextBox id="txt-update-zipcode" name="zipCode" value={zipcode}
                                label="Zip Code *" labelUp parentModal="drawer-update-address"
                                disabled={verified}
                                onTextChanges={zip => {
                                    window.hideFormError("zipCode", "drawer-update-address");
                                    if (zip.length > 4) {
                                        hub.LookUpZipcode(zip, country?.alpha3Code ?? "usa", res => {
                                            zipcodeSet(zip);
                                            selectedStateSet(res?.address?.stateId);
                                        })
                                    }
                                    else {
                                        zipcodeSet(zip);
                                        selectedStateSet(undefined);
                                    }
                                }} />
                        </div>
                        <div className="col-md-6">
                            <DropDown id="drp-update-states"
                                name="stateId"
                                forceSelectValue={selectedState}
                                source={states} parentModal="drawer-update-address"
                                disabled={verified}
                                onSelect={e => {
                                    selectedStateSet(e);
                                    window.hideFormError("stateId", "drawer-update-address");
                                }}
                                label="State *" labelUp />
                        </div>
                    </div>
                </div>

                {
                    showMap &&
                    <Map id="job-updateaddress-map" height={400}
                        client={address} preventCenterizeOnLoad
                        onZoomChanged={z => zoomSet(z)}
                        onClientUpdated={e => addressSet(e)} />
                }

                {/* breathing room so the action buttons don't hug the map */}
                <div className="mt-8" />

            </DrawerForm>

        </>
    )

}